/* copied and adjusted code from Desp code header.js, feather-icons imported instead of feather.js */
import { Component, OnInit, AfterViewInit, OnDestroy } from "@angular/core";
import feather from 'feather-icons';
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { RouterModule } from "@angular/router";
import { Subject, Subscription, takeUntil } from "rxjs";
import { AuthService } from "../../../auth/services/auth.service";
import { DOCUMENTATION_HEADER_LINK } from "../../config/environment-config";

@Component({
  selector: "app-header",
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.css"],
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  backgroundColor!: string;
  backgroundColorTransparent!: string;
  headerHeight!: string;
  userIsAuthenticated = false;
  headerHasRevealed = false;
  hideMenuTimeout = 4000;
  tempPrevPos = 0;
  dropdownIsVisible = false;
  hidingTimeoutId: any;
  hidingInfoMenuId: any;
  hidingUserMenuId: any;
  previousScrollY = 0;
  headerVisible = true;
  currentLink: string = "";
  readonly DOC_LINK = DOCUMENTATION_HEADER_LINK;

  private currentLinkSubscription!: Subscription;
  preferredUsername: string | null = null;
  private routerContent!: HTMLElement;
  private destroy$ = new Subject<void>();

  constructor(private router: Router,
     private authService: AuthService
    ) {}

  //lifecycle hooks
  ngOnInit() {
    this.getCSSVariables();
    this.fetchLoggedUsername();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      feather.replace();
      this.routerContent = document.querySelector(
        ".router-content"
      ) as HTMLElement;

      if (this.routerContent) {
        this.routerContent.addEventListener(
          "scroll",
          this.scrollListener.bind(this)
        );
      }
      // to close menu on outside click
      document.addEventListener("click", this.onGlobalClick.bind(this));
      this.setupMenuIcon();
    }, 0);
  }

  // auto-unsubscribe
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.routerContent) {
      this.routerContent.removeEventListener("scroll", this.scrollListener);
    }
    document.removeEventListener("click", this.onGlobalClick.bind(this));
  }

  fetchLoggedUsername() {
    // Check if the user is already cached in localStorage
    const loggedUser = this.authService.getLoggedUserLocalStorage();
    if (loggedUser) {
      this.preferredUsername = loggedUser.preferred_username;
      // console.log("Logged user from localStorage:", loggedUser);
    } else {
      // Fetch the raw token from Keycloak and log it
      this.authService
        .getTokenNoAsync()
        .then((token) => {
          //  console.log("Raw token:", token);

          // Decode the token to extract user information
          const decodedToken = JSON.parse(atob(token.split(".")[1]));
          // console.log("Decoded token payload:", decodedToken);

          this.preferredUsername = decodedToken.preferred_username;
          // cache the user and token in localStorage
          this.authService.setLoggedUserOnLocalStorage(token);
        })
        .catch((error) => {
          console.error("Error fetching or decoding token:", error);
        });
    }
  }

  logout(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.authService.logout();
  } 

  getCSSVariables() {
    const bodyStyles = getComputedStyle(document.body);
    this.backgroundColor = bodyStyles.getPropertyValue(
      "--desp-background-color"
    );
    this.backgroundColorTransparent = bodyStyles.getPropertyValue(
      "--desp-background-color-transparent"
    );
    this.headerHeight =
      bodyStyles.getPropertyValue("--header-height") || "64px";
  }

  showHeader() {
    const header = document.getElementById("desp-header-container");
    if (header && !this.headerVisible) {
      header.style.transition = "top 250ms ease-in-out";
      header.style.top = "0";
      this.headerVisible = true;
    }
  }

  hideHeader() {
    const header = document.getElementById("desp-header-container");
    if (header && this.headerVisible) {
      header.style.transition = "top 250ms ease-in-out";
      header.style.top = `-${header.offsetHeight}px`;
      this.headerVisible = false;
    }
  }

  private scrollListener(event: Event) {
    const target = event.target as HTMLElement;
    const currentScrollY = target.scrollTop;
    const deltaY = currentScrollY - this.tempPrevPos;

    if (deltaY > 50) {
      // scrolled down more than 50px
      this.hideHeader();
      this.tempPrevPos = currentScrollY;
    } else if (deltaY < -50) {
      // scrolled up more than 50px
      this.showHeader();
      this.tempPrevPos = currentScrollY;
    }
  }

  onUserAuthenticationChange() {
    const loggedElements = document.getElementsByClassName(
      "logged"
    ) as HTMLCollectionOf<HTMLElement>;
    const notLoggedElements = document.getElementsByClassName(
      "not-logged"
    ) as HTMLCollectionOf<HTMLElement>;

    if (this.userIsAuthenticated) {
      Array.from(loggedElements).forEach((el) => (el.style.display = "flex"));
      Array.from(notLoggedElements).forEach(
        (el) => (el.style.display = "none")
      );
    } else {
      Array.from(loggedElements).forEach((el) => (el.style.display = "none"));
      Array.from(notLoggedElements).forEach(
        (el) => (el.style.display = "flex")
      );
    }
  } 

  toggleHeaderInfoMenu() {
    const headerInfoFrame = document.getElementById("info-frame");
    if (headerInfoFrame) {
      const isVisible = headerInfoFrame.style.display === "flex";
      if (isVisible) {
        headerInfoFrame.style.display = "none";
        headerInfoFrame.style.opacity = "0.0";
        clearTimeout(this.hidingInfoMenuId);
      } else {
        headerInfoFrame.style.display = "flex";
        headerInfoFrame.style.opacity = "1.0";
        clearTimeout(this.hidingInfoMenuId);
        this.hidingInfoMenuId = setTimeout(() => {
          headerInfoFrame.style.display = "none";
          headerInfoFrame.style.opacity = "0.0";
        }, this.hideMenuTimeout);
      }
    }
  }

  toggleHeaderUserMenu() {
    const headerUserFrame = document.getElementById("user-frame");
    if (headerUserFrame) {
      const isVisible = headerUserFrame.style.display === "flex";
      if (isVisible) {
        headerUserFrame.style.display = "none";
        headerUserFrame.style.opacity = "0.0";
        clearTimeout(this.hidingUserMenuId);
      } else {
        headerUserFrame.style.display = "flex";
        headerUserFrame.style.opacity = "1.0";
        clearTimeout(this.hidingUserMenuId);
        this.hidingUserMenuId = setTimeout(() => {
          headerUserFrame.style.display = "none";
          headerUserFrame.style.opacity = "0.0";
        }, this.hideMenuTimeout);
      }
    }
  }
  onGlobalClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const infoMenu = document.getElementById("info-frame");
    const infoButton = document.getElementById("info-button");

    const userMenu = document.getElementById("user-frame");
    const userButton = document.getElementById("login-div");

    if (
      infoMenu &&
      !infoMenu.contains(target) &&
      infoButton &&
      !infoButton.contains(target)
    ) {
      infoMenu.style.display = "none";
      infoMenu.style.opacity = "0.0";
    }

    if (
      userMenu &&
      !userMenu.contains(target) &&
      userButton &&
      !userButton.contains(target)
    ) {
      userMenu.style.display = "none";
      userMenu.style.opacity = "0.0";
    }
  }

  setupMenuIcon() {
    const menuIcon = document.getElementById("menu-icon-container");
    if (menuIcon) {
      menuIcon.addEventListener("click", () => this.toggleDropdownMenu());
    } else {
      console.error("Menu icon container not found");
    }
  }

  toggleDropdownMenu() {
    const dropdownMenuContainer = document.getElementById(
      "dropdown-menu-container"
    );
    const menuIcon = document.getElementById("menu-icon");

    if (dropdownMenuContainer && menuIcon) {
      if (this.dropdownIsVisible) {
        // hide the menu
        menuIcon.setAttribute("data-feather", "menu");
        dropdownMenuContainer.classList.remove("show");
      } else {
        // show the menu
        menuIcon.setAttribute("data-feather", "x");
        dropdownMenuContainer.classList.add("show");
      }
      feather.replace(); // update feather icons
      this.dropdownIsVisible = !this.dropdownIsVisible;
    } else {
      console.error("Dropdown menu container or menu icon not found");
    }
  }

  private hideDropdownMenu(): void {
    const dropdownMenuContainer = document.getElementById(
      "dropdown-menu-container"
    );
    const menuIcon = document.getElementById("menu-icon");
    if (dropdownMenuContainer && menuIcon) {
      menuIcon.setAttribute("data-feather", "menu");
      dropdownMenuContainer.classList.remove("show");
      this.dropdownIsVisible = false;
    }
  }

  goToConsumption(event: Event): void {
    event.preventDefault(); // prevent anchor default
    this.router.navigate(["/consumption"]);
  }
}
